import { CartesianGrid, LabelList, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

export function formatChartSeconds(value) {
    if (!Number.isFinite(value)) return "";
    const minutes = Math.floor(value / 60);
    const seconds = value - minutes * 60;
    return minutes > 0 ? `${minutes}:${seconds.toFixed(2).padStart(5, "0")}` : seconds.toFixed(2);
}

export default function TimeProgressChart({ records }) {
    const data = (records || [])
        .slice()
        .sort((a, b) => (a.record_date || "").localeCompare(b.record_date || ""))
        .map((r) => ({
            date: (r.record_date || "").slice(5).split("-").reverse().join("/"),
            seconds: (r.time_ms || 0) / 1000,
            time_text: r.time_text,
            event: r.event_number,
            note: r.note || "",
        }));
    if (data.length === 0) return null;
    // Rentang sumbu menyesuaikan selisih waktu: selisih kecil -> grafik "zoom in" agar
    // kenaikan/penurunan tetap terlihat jelas. Sumbu dibalik: garis naik = makin kencang.
    const values = data.map((d) => d.seconds);
    const minValue = Math.min(...values);
    const maxValue = Math.max(...values);
    const range = maxValue - minValue;
    const pad = Math.max(range * 0.35, 0.05);
    const domain = [Math.max(0, minValue - pad), maxValue + pad];
    const single = data.length === 1;
    const hasNotes = data.some((d) => d.note);

    const renderDot = (props) => {
        const { cx, cy, payload, index } = props;
        const noted = Boolean(payload.note);
        return (
            <circle
                key={`dot-${index}`}
                cx={cx}
                cy={cy}
                r={noted ? 5 : 3.5}
                fill="#E02424"
                stroke={noted ? "#FFB800" : "none"}
                strokeWidth={noted ? 2.5 : 0}
            />
        );
    };

    const renderTooltip = ({ active, payload }) => {
        if (!active || !payload || !payload.length) return null;
        const point = payload[0].payload;
        return (
            <div className="bg-white border border-racing-line px-3 py-2 text-xs shadow-md max-w-[220px]">
                <p className="text-racing-muted">{point.date}{point.event ? ` • ${point.event}` : ""}</p>
                <p className="font-display text-lg text-racing-red">{point.time_text}</p>
                {point.note && <p className="mt-1 text-neutral-800 whitespace-pre-line">Catatan: {point.note}</p>}
            </div>
        );
    };

    return (
        <div>
            <ResponsiveContainer width="100%" height={200}>
                <LineChart data={data} margin={{ top: 26, right: 30, bottom: 4, left: 0 }}>
                    <CartesianGrid stroke="#EAE3D8" vertical={false} />
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#6E675C" }} tickLine={false} axisLine={{ stroke: "#EAE3D8" }} padding={{ left: 20, right: 20 }} />
                    <YAxis
                        reversed
                        tickFormatter={formatChartSeconds}
                        tick={{ fontSize: 10, fill: "#6E675C" }}
                        tickLine={false}
                        axisLine={false}
                        width={54}
                        tickCount={6}
                        domain={single ? [Math.max(0, minValue - 1), maxValue + 1] : domain}
                    />
                    <Tooltip content={renderTooltip} />
                    <Line type="monotone" dataKey="seconds" stroke="#E02424" strokeWidth={2.5} dot={renderDot} activeDot={{ r: 6 }}>
                        <LabelList dataKey="time_text" position="top" style={{ fontSize: 10, fill: "#B91C1C", fontWeight: 600 }} />
                    </Line>
                </LineChart>
            </ResponsiveContainer>
            {hasNotes && (
                <p className="px-2 pb-1 text-[10px] text-racing-muted flex items-center gap-1.5" data-testid="time-chart-note-legend">
                    <span className="inline-block w-2.5 h-2.5 rounded-full bg-racing-red border-2 border-racing-gold" />
                    Titik bertepi emas memiliki catatan khusus dari pelatih — arahkan/sentuh untuk membacanya.
                </p>
            )}
        </div>
    );
}
